import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Divider,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as BackIcon,
  LocationOn as LocationIcon,
  ViewModule as ShelfIcon,
  Book as BookIcon,
  Home as HomeIcon,
  LibraryBooks as LibraryIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { bibliotequesAPI, rayonnagesAPI } from '../services/api';

function RayonnageCard({ rayonnage, onViewBooks, onEdit, onDelete }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (onEdit) {
      navigate(`/rayonnages/${rayonnage.id}/modifier`);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(rayonnage);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        px: 3,
        py: 1.5,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        '&:last-of-type': {
          borderBottom: 'none',
        },
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      {/* Contenu */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {/* Nom */}
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontWeight: 700,
            fontSize: '16px',
            m: 0,
            mb: 0.25,
            lineHeight: 1.2,
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          }}
        >
          {rayonnage.nom}
        </Typography>

        {/* Description */}
        {rayonnage.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 0,
              mt: 0,
              m: 0,
              lineHeight: 1.3,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {rayonnage.description}
          </Typography>
        )}
      </Box>

      {/* Nombre de livres et bouton de suppression */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexShrink: 0 }}>
        <Box 
          onClick={(e) => {
            e.stopPropagation();
            onViewBooks(rayonnage.id);
          }}
          sx={{ 
            textAlign: 'right', 
            px: 2,
            py: 1,
            borderRadius: 2,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.15)',
              transform: 'scale(1.05)',
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 400,
              color: 'primary.main',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {rayonnage.nombre_livres || 0}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {rayonnage.nombre_livres === 1 
              ? t('bibliothequeDetailPage.bookCount', { count: rayonnage.nombre_livres || 0 })
              : t('bibliothequeDetailPage.bookCountPlural', { count: rayonnage.nombre_livres || 0 })}
          </Typography>
        </Box>
        <IconButton
          size="small"
          color="error"
          onClick={handleDelete}
          sx={{
            flexShrink: 0,
          }}
        >
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

function BibliothequeDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteDialog, setDeleteDialog] = useState({ open: false, rayonnage: null });
  const [deleteBibliothequeDialog, setDeleteBibliothequeDialog] = useState(false);

  // Requête pour récupérer les détails de la bibliothèque
  const { data: bibliotheque, isLoading: bibliothequeLoading, error: bibliothequeError } = useQuery(
    ['bibliotheque', id],
    () => bibliotequesAPI.getById(id).then(res => res.data.data),
    {
      enabled: !!id,
    }
  );

  // Requête pour récupérer les rayonnages de cette bibliothèque
  const { data: rayonnages, isLoading: rayonnagesLoading, error: rayonnagesError } = useQuery(
    ['bibliotheque-rayonnages', id],
    () => rayonnagesAPI.getByBibliotheque(id).then(res => res.data.data),
    {
      enabled: !!id,
    }
  );

  const getInitials = (nom) => {
    const words = nom.split(' ');
    if (words.length >= 2) {
      return `${words[0].charAt(0).toUpperCase()}${words[words.length - 1].charAt(0).toUpperCase()}`;
    }
    return nom.charAt(0).toUpperCase();
  };

  const handleViewBooks = (rayonnageId) => {
    navigate(`/livres?rayonnage_id=${rayonnageId}`);
  };

  const handleDelete = (rayonnage) => {
    setDeleteDialog({ open: true, rayonnage });
  };

  // Mutation pour supprimer un rayonnage
  const deleteRayonnageMutation = useMutation(
    (rayonnageId) => rayonnagesAPI.delete(rayonnageId),
    {
      onSuccess: () => {
        toast.success(t('bibliothequeDetailPage.shelfDeleteSuccess'));
        queryClient.invalidateQueries(['bibliotheque', id]);
        queryClient.invalidateQueries(['bibliotheque-rayonnages', id]);
        queryClient.invalidateQueries('rayonnages');
        queryClient.invalidateQueries('rayonnages-list');
        queryClient.invalidateQueries('bibliotheques');
        setDeleteDialog({ open: false, rayonnage: null });
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          toast.error(t('bibliothequeDetailPage.shelfDeleteError'));
        } else {
          toast.error(t('bibliothequeDetailPage.shelfDeleteGenericError'));
        }
        setDeleteDialog({ open: false, rayonnage: null });
      }
    }
  );

  const confirmDelete = () => {
    if (deleteDialog.rayonnage) {
      deleteRayonnageMutation.mutate(deleteDialog.rayonnage.id);
    }
  };

  const handleDeleteBibliotheque = () => {
    setDeleteBibliothequeDialog(true);
  };

  // Mutation pour supprimer une bibliothèque
  const deleteBibliothequeMutation = useMutation(
    (bibliothequeId) => bibliotequesAPI.delete(bibliothequeId),
    {
      onSuccess: () => {
        toast.success(t('bibliothequeDetailPage.deleteSuccess'));
        queryClient.invalidateQueries('bibliotheques');
        queryClient.invalidateQueries('bibliotheques-list');
        queryClient.invalidateQueries('rayonnages');
        queryClient.invalidateQueries('rayonnages-list');
        navigate('/bibliotheques');
      },
      onError: (error) => {
        if (error.response?.status === 409) {
          toast.error(t('bibliothequeDetailPage.deleteError'));
        } else {
          toast.error(t('bibliothequeDetailPage.deleteGenericError'));
        }
        setDeleteBibliothequeDialog(false);
      }
    }
  );

  const confirmDeleteBibliotheque = () => {
    if (id) {
      deleteBibliothequeMutation.mutate(id);
    }
  };

  if (bibliothequeLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (bibliothequeError) {
    return (
      <Alert severity="error">
        {t('bibliothequeDetailPage.loadingError', { message: bibliothequeError.message })}
      </Alert>
    );
  }

  if (!bibliotheque) {
    return (
      <Alert severity="info">
        {t('bibliothequeDetailPage.notFound')}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header avec navigation */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <IconButton onClick={() => navigate('/bibliotheques')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="h1" 
            sx={{ 
              flexGrow: 1, 
              textAlign: 'center',
              fontSize: '18px',
              fontWeight: 500,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('bibliothequeDetailPage.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/bibliotheques/${id}/modifier`)}
              size="small"
              sx={{ 
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {t('bibliothequeDetailPage.edit')}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteBibliotheque}
              size="small"
              sx={{ 
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {t('bibliothequeDetailPage.delete')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Informations de la bibliothèque */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            mb: 1,
            fontWeight: 700,
            fontSize: '24px',
            fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          }}
        >
          {bibliotheque.nom}
        </Typography>
        {bibliotheque.description && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 3,
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {bibliotheque.description}
          </Typography>
        )}

        {/* Cartes de statistiques */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Carte Livres */}
          <Box
            sx={{
              flex: '1 1 auto',
              minWidth: '120px',
              p: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: '32px',
                fontWeight: 700,
                color: 'primary.main',
                mb: 0.5,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {bibliotheque.nombre_livres || 0}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {(bibliotheque.nombre_livres || 0) === 1 
                ? t('bibliothequeDetailPage.bookCount')
                : t('bibliothequeDetailPage.bookCountPlural')}
            </Typography>
          </Box>

          {/* Carte Rayonnages */}
          <Box
            sx={{
              flex: '1 1 auto',
              minWidth: '120px',
              p: 2,
              borderRadius: 2,
              backgroundColor: 'rgba(46, 125, 50, 0.08)',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontSize: '32px',
                fontWeight: 700,
                color: 'success.main',
                mb: 0.5,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {bibliotheque.nombre_rayonnages || 0}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'success.main',
                fontWeight: 500,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {(bibliotheque.nombre_rayonnages || 0) === 1 
                ? t('bibliothequeDetailPage.shelfCount')
                : t('bibliothequeDetailPage.shelfCountPlural')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Section Rayonnages */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: '18px',
                  mb: 0.5,
                  fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                }}
              >
                {t('bibliothequeDetailPage.shelves')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                }}
              >
                {t('bibliothequeDetailPage.shelvesDescription')}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(`/rayonnages/ajouter?bibliotheque_id=${id}`)}
              size="small"
              sx={{
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {t('bibliothequeDetailPage.add')}
            </Button>
          </Box>
        </Box>
        <Divider />
        {rayonnagesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rayonnagesError ? (
          <Alert severity="error" sx={{ m: 3 }}>
            {t('bibliothequeDetailPage.shelvesLoadingError', { message: rayonnagesError.message })}
          </Alert>
        ) : rayonnages && rayonnages.length > 0 ? (
          <Box>
            {rayonnages.map((rayonnage) => (
              <RayonnageCard
                key={rayonnage.id}
                rayonnage={rayonnage}
                onViewBooks={handleViewBooks}
                onEdit={true}
                onDelete={handleDelete}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ 
            textAlign: 'center', 
            py: 4,
            px: 3,
            color: 'text.secondary'
          }}>
            <ShelfIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
            <Typography variant="body1">
              {t('bibliothequeDetailPage.noShelves')}
            </Typography>
            <Button
              variant="contained"
              size="small"
              sx={{ mt: 2 }}
              onClick={() => navigate(`/rayonnages/ajouter?bibliotheque_id=${id}`)}
            >
              {t('bibliothequeDetailPage.addShelf')}
            </Button>
          </Box>
        )}
      </Card>

      {/* Dialog de confirmation de suppression de rayonnage */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, rayonnage: null })}>
        <DialogTitle>{t('bibliothequeDetailPage.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('bibliothequeDetailPage.shelfDeleteConfirm', { name: deleteDialog.rayonnage?.nom })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, rayonnage: null })}>
            {t('bibliothequeDetailPage.cancel')}
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleteRayonnageMutation.isLoading}
          >
            {deleteRayonnageMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              t('bibliothequeDetailPage.delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression de bibliothèque */}
      <Dialog open={deleteBibliothequeDialog} onClose={() => setDeleteBibliothequeDialog(false)}>
        <DialogTitle>{t('bibliothequeDetailPage.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('bibliothequeDetailPage.deleteConfirm', { name: bibliotheque?.nom })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteBibliothequeDialog(false)}>
            {t('bibliothequeDetailPage.cancel')}
          </Button>
          <Button 
            onClick={confirmDeleteBibliotheque} 
            color="error" 
            variant="contained"
            disabled={deleteBibliothequeMutation.isLoading}
          >
            {deleteBibliothequeMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              t('bibliothequeDetailPage.delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BibliothequeDetailPage;

